import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';

import { deleteStoryThunk, getStoryListThunk, setMassSortStoryThunk } from 'redux/thunks/story';
import { RootState, useDispatch, useSelector } from '../../redux/store';
import { DroppableDataTable } from 'components/DroppableDataTable';
import { TableHead } from 'components/table/TableHeadCustom';
import { ConfirmDialog } from 'components/ConfirmDialog';
import { setStoryListParams } from 'redux/slices/story';
import { ListHeader } from 'components/ListHeader/ListHeader';
import { PATH_DASHBOARD } from '../../routes/paths';
import { StoryTableRow } from 'sections/story/StoryTableRow';
import { Story, StoryList as StoryListType } from 'types/story';
import { LoadingScreen } from 'components/LoadingScreen';
import { useDialog } from 'hooks/useDialog';

const TABLE_HEAD: TableHead = {
  image: { label: 'Фото' },
  name: { label: 'Название' },
  created_at: { label: 'Дата создания' },
  controls: { label: '' },
};

export const StoryList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const storyList = useSelector((state: RootState) => state.story.storyList);
  const storyListParams = useSelector((state: RootState) => state.story.storyListParams);

  const { dialog, dialogDispatch, onOpen, onClose } = useDialog();
  const { enqueueSnackbar } = useSnackbar();
  const [storyListDroppable, setStoryListDroppable] = useState<Story[]>(storyList?.data || []);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const load = async () => {
      const res = await dispatch(getStoryListThunk(storyListParams));

      const newStoryList = res.payload as StoryListType;

      if (res && newStoryList.data) setStoryListDroppable(newStoryList.data);

      setLoaded(true);
    };

    load();
  }, [dispatch, storyListParams]);

  const handleDeleteRow = useCallback(
    async (id: number) => {
      await dispatch(deleteStoryThunk(id));

      onClose();

      enqueueSnackbar(`История ${id} удалена`);

      const res = await dispatch(getStoryListThunk(storyListParams));

      const newStoryList = res.payload as StoryListType;

      setStoryListDroppable(newStoryList?.data || []);
    },
    [dispatch, enqueueSnackbar, onClose, storyListParams]
  );

  const onDeleteRowClickHandler = useCallback(
    (id: number) => {
      dialogDispatch({
        type: 'ALL',
        payload: {
          ...dialog,
          main: `Вы действительно хотите удалить историю ${id}?`,
          footer: () => handleDeleteRow(id),
        },
      });

      onOpen();
    },
    [dialog, dialogDispatch, handleDeleteRow, onOpen]
  );

  const handleEditRow = useCallback(
    (id: number) => {
      navigate(PATH_DASHBOARD.story.info(id));
    },
    [navigate]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      dispatch(
        setStoryListParams({
          ...storyListParams,
          page,
        })
      );
    },
    [dispatch, storyListParams]
  );

  const handleSaveItems = useCallback(
    (items: Story[]) => {
      const paramSortedList = items.map((item, index) => ({
        id: item.id,
        sort: index + 1,
      }));

      dispatch(setMassSortStoryThunk(paramSortedList));
    },
    [dispatch]
  );

  const stories =
    storyListDroppable &&
    storyListDroppable.map((row, index) => (
      <StoryTableRow
        key={row.id}
        row={row}
        index={index}
        onDeleteRow={onDeleteRowClickHandler}
        onEditRow={handleEditRow}
      />
    ));

  return (
    <>
      {loaded ? (
        <>
          <ListHeader link={{ name: 'Группу историй', href: PATH_DASHBOARD.story.new }} />

          {storyList.data && (
            <DroppableDataTable
              tableHead={TABLE_HEAD}
              pagination={storyList.meta}
              params={storyListParams}
              onPage={handlePageChange}
              items={storyListDroppable}
              onSetItems={setStoryListDroppable}
              saveCallback={handleSaveItems}
            >
              {stories}
            </DroppableDataTable>
          )}
        </>
      ) : (
        <LoadingScreen />
      )}

      <ConfirmDialog dialog={dialog} open={dialog.open} onClose={onClose} />
    </>
  );
};
